<template>
  <div>
    <b-row v-if="showFilters"  class="pt-2 pb-1">
      <b-col v-if="!embedded" cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col :cols="embedded ? 12 : 6" class="pr-3 text-right">
        <button-filters
          :modal-id="filterModalId"
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <filter-modal
      v-if="showFilters"
      :modal-id="filterModalId"
      @action="buildFilters()"
      @clear="clearSearchFilters()"
    >
      <template v-slot:main-filters>
        <filters-component
          :event-definition-options="eventDefinitionOptions"
          :event-definition-options-filter.sync="eventDefinitionOptionsFilter"
          :event-code-filter.sync="eventCodeFilter"
          :panel-options="panelOptions"
          :panel-filter.sync="panelFilter"
          :start-datetime.sync="startDatetimeFilter"
          :end-datetime.sync="endDatetimeFilter"
          :type-alarm-options="alarmTypeOptions"
          :type-alarm-filter.sync="typeAlarmFilter"
          :recovered-filter.sync="recoveredFilter"
          :facilities-filter.sync="facilitiesFilter"
        />
      </template>
    </filter-modal>

    <Transition name="slide-right-left">
      <div v-if="embedded && isFilterPanelOpen" class="filters-panel-fullscreen">
        <div class="filters-panel-header">
          <p class="h4 text-capitalize mb-2 text-white">
            {{ $t("messages.info.advanced_search") }}
          </p>
          <b-button variant="link" size="sm" class="text-dark p-0" @click="isFilterPanelOpen = false">
            <feather-icon icon="XIcon" size="24" />
          </b-button>
        </div>
        <div class="filters-panel-content">
          <filters-component
            :event-definition-options="eventDefinitionOptions"
            :event-definition-options-filter.sync="eventDefinitionOptionsFilter"
            :event-code-filter.sync="eventCodeFilter"
            :panel-options="panelOptions"
            :panel-filter.sync="panelFilter"
            :start-datetime.sync="startDatetimeFilter"
            :end-datetime.sync="endDatetimeFilter"
            :type-alarm-options="alarmTypeOptions"
            :type-alarm-filter.sync="typeAlarmFilter"
            :recovered-filter.sync="recoveredFilter"
            :facilities-filter.sync="facilitiesFilter"
          />
        </div>
        <div class="filters-panel-footer">
          <b-button variant="outline-primary" size="sm" class="text-capitalize" @click="clearEmbeddedFilters">
            {{ $t("messages.info.clear_filters") }}
          </b-button>
          <b-button variant="primary" size="sm" class="text-capitalize" @click="applyEmbeddedFilters">
            {{ $t("search") }} <feather-icon icon="SearchIcon" />
          </b-button>
        </div>
      </div>
    </Transition>

    <!-- Table Container Card -->
    <alarms-logs-component
      v-show="!embedded || !isFilterPanelOpen"
      ref="refListTable"
      :currentPage="currentPage"
      :dataMeta="dataMeta"
      :fetchList="fetchList"
      :isSortDirDesc="isSortDirDesc"
      :perPage="perPage"
      :perPageOptions="perPageOptions"
      :searchQuery="searchQuery"
      :show-filters="showFilters"
      :embedded="embedded"
      show-export
      :exporting="disableExport"
      :sortBy="sortBy"
      :tableColumns="tableColumns"
      :totalList="totalList"
      @per-page="perPageValue"
      @update="update"
      @refresh-data="refreshData"
      @export="exportExcel"
    >
      <template #search>
        <FacetedSearch
          :options="searchOptions"
          class="mr-1 dmp-logs-search-input"
          @search="setFacetedFilters"
        />
        <b-button-group v-if="embedded" class="mr-1">
          <b-button class="buttonFilter cursor-pointer d-flex align-items-center" data-cy="filter-btn"
            @click="isFilterPanelOpen = true">
            <b-icon-filter-circle-fill scale="1.1" />
            <span class="align-middle ml-1 text-capitalize">{{ $t("filters") }}</span>
          </b-button>
          <toggle-clear-filter :filters-count="filtersCount" @clear-search-filters="clearSearchFilters" />
        </b-button-group>
      </template>
    </alarms-logs-component>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
  BInputGroup,
  BButtonGroup,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, watch } from "@vue/composition-api";
import useList from "./useList";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import Pagination from "@/components/Pagination.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import FiltersComponent from "../components/FiltersComponent";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import AlarmsLogsComponent from "../components/AlarmsLogsComponent.vue";
import FacetedSearch from "@/components/FacetedSearch.vue";
import ToggleClearFilter from "@/components/Filters/ToggleClearFilter.vue";

export default {
  components: {
    Pagination,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BInputGroup,
    BButtonGroup,
    vSelect,
    ToastificationContent,
    BreadcrumbMenu,
    FiltersComponent,
    ButtonFilters,
    FilterModal,
    AlarmsLogsComponent,
    FacetedSearch,
    ToggleClearFilter,
  },
  props: {
    filters: {
      type: Object | Array,
    },
    showFilters: {
      type: Boolean,
      default: true,
    },
    slotFilters: {
      type: Object | Array,
      default: () => ({})
    },
    slotId: {
      type: String,
      default: null,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    filterModalId() {
      return this.embedded
        ? `dmp-logs-filter-modal-${this.slotId || this._uid}`
        : 'filter_modal';
    },
    searchOptions() {
      return [
        {
          label: this.$t('dmp.eventDefinition'),
          value: 'event_definition',
          isArray: false,
        },
        {
          label: this.$t('dmp.eventType'),
          value: 'event_type',
          isArray: false,
        },
        {
          label: this.$t('dmp.deviceType'),
          value: 'device_type',
          isArray: false,
        },
        {
          label: this.$t('dmp.deviceId'),
          value: 'device_id',
          isArray: false,
        },
        {
          label: this.$t('dmp.deviceName'),
          value: 'device_name',
          isArray: false,
        },
        {
          label: this.$t('dmp.areaId'),
          value: 'area_id',
          isArray: false,
        },
        {
          label: this.$t('dmp.area'),
          value: 'area_name',
          isArray: false,
        },
        {
          label: this.$t('dmp.username'),
          value: 'user_name',
          isArray: false,
        },
        {
          label: this.$t('dmp.description'),
          value: 'description',
          isArray: false,
        },
      ];
    },
  },
  setup(props, context) {
    const isFilterPanelOpen = ref(false);
    //start pagination
    const perPageValue = (data) => {
      perPage.value = data;
    };

    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination

    const { t } = useI18nUtils();

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      clearSearchFilters,
      buildFilters,
      filters,
      filtersCount,
      idDmpProfile,
      eventCode,
      typeAlarm,
      eventCodeFilter,
      dispatchAlarmFilter,
      valueFilter,
      facilitiesFilter,

      alarmTypeOptions,
      eventDefinitionOptions,
      eventDefinitionOptionsFilter,
      panelFilter,
      panelOptions,
      startDatetimeFilter,
      endDatetimeFilter,
      typeAlarmFilter,
      recoveredFilter,
      deviceIdFilter,
      deviceNameFilter,
      action,
      disableExport,
      setFacetedFilters,
    } = useList();

    const itemFilter = ref();

    const addItemFilter = (data) => {
      itemFilter.value = data;
    };
    const addItemFilterSearch = (data) => {
      data.forEach((element) => {
        if (nameNav.value === element.text) {
          searchQuery.value = element.searchQuery;
        }
      });
    };
    const nameNav = ref("breadcrumbs.iot.dmp.log");

    // On Click the refresh button
    const refreshData = () => refetchData();

    const applyEmbeddedFilters = () => {
      buildFilters();
      isFilterPanelOpen.value = false;
    };

    const clearEmbeddedFilters = () => {
      clearSearchFilters();
      isFilterPanelOpen.value = false;
    };

    const exportExcel = () => {
      action.value = "export";
      disableExport.value = true;
      fetchList();
    };

    // Accepts both shapes; an empty one means "no slot filters".
    const slotFiltersSource = () => {
      const f = props.slotFilters?.filters ?? props.slotFilters
      return f && Object.keys(f).length ? f : null
    }
    let slotFiltersApplied = false

    watch(
      slotFiltersSource,
      (val) => {
        if (!val) return
        const f = val
        panelFilter.value = f.panelFilter || []
        eventDefinitionOptionsFilter.value = f.eventDefinitionOptionsFilter || []
        eventCodeFilter.value = f.eventCodeFilter || []
        startDatetimeFilter.value = f.startDatetime || null
        endDatetimeFilter.value = f.endDatetime || null
        // null, not false: false narrows the slot to non-recovered logs.
        recoveredFilter.value = f.recoveredFilter ?? null
        deviceIdFilter.value = f.device_id?.length ? f.device_id : null
        deviceNameFilter.value = f.device_name?.length ? f.device_name : null
        facilitiesFilter.value = f.facilitiesFilter || []
        filters.value = {
          id_type_alarm: typeAlarmFilter.value,
          event_definition: eventDefinitionOptionsFilter.value,
          event_type: eventCodeFilter.value,
          id_dmp_panel: panelFilter.value,
          start_date: startDatetimeFilter.value,
          end_date: endDatetimeFilter.value,
          is_recovered: recoveredFilter.value,
          facilities: facilitiesFilter.value,
        }

        // The first run precedes the table's own fetch; later ones need a reload.
        if (slotFiltersApplied) refetchData()
        slotFiltersApplied = true
      },
      {
        immediate: true,
        deep: true
      }
    )

    return {
      setFacetedFilters,

      nameNav,
      addItemFilter,
      itemFilter,
      addItemFilterSearch,

      update,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,

      clearSearchFilters,
      buildFilters,
      filters,
      filtersCount,

      idDmpProfile,
      eventCode,
      typeAlarm,
      eventCodeFilter,
      typeAlarmFilter,
      dispatchAlarmFilter,
      valueFilter,
      facilitiesFilter,

      alarmTypeOptions,
      eventDefinitionOptions,
      eventDefinitionOptionsFilter,
      panelOptions,
      panelFilter,
      startDatetimeFilter,
      endDatetimeFilter,
      recoveredFilter,
      disableExport,

      perPageValue,
      refreshData,
      exportExcel,
      isFilterPanelOpen,
      applyEmbeddedFilters,
      clearEmbeddedFilters,
    };
  },
};
</script>

<style scoped lang="scss">
.dmp-logs-search-input {
  flex: 1 1 0;
  min-width: 190px;
}

.filters-panel-fullscreen {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  z-index: 10;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.filters-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 0;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.filters-panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 0 1.5rem;
}

.filters-panel-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  flex-shrink: 0;
}

.dark-layout {
  .filters-panel-fullscreen {
    background-color: #283046;
    color: #b4b7bd;
  }

  .filters-panel-header {
    border-bottom-color: rgba(255, 255, 255, 0.05);
  }

  .filters-panel-footer {
    border-top-color: #3b4253;
  }
}
</style>
